/*
 * @copyright Copyright (c) valantic CEC Deutschland GmbH (https://www.valantic.com)
 * @see PROJECT_LICENSE.txt
 */

import { safeJsonParse } from './utils.js'

/**
 * Normalize a raw image entry to a consistent shape
 *
 * @param {Object|string|null} entry
 * @param {number} index
 * @returns {{fallback: (Object|null), image: (Object|null), label: (string|null), position: number, small_image: (Object|null), thumbnail: (Object|null)}|null}
 */
const normalizeLiveSearchImageEntry = (entry, defaultPosition = 1) => {
    if (!entry || typeof entry !== 'object') {
        return null
    }

    const position = Number.isFinite(entry.position)
        ? Number(entry.position)
        : Number.isFinite(entry?.image?.position)
            ? Number(entry.image.position)
            : defaultPosition

    const normalizeSource = (value) => {
        if (!value) {
            return null
        }
        if (typeof value === 'string') {
            return { url: value }
        }
        if (typeof value === 'object' && value.url) {
            return value
        }
        return null
    }

    const normalized = {
        position: position || defaultPosition,
        label: entry.caption ||
            entry.label ||
            entry?.image?.label ||
            entry?.small_image?.label ||
            entry?.thumbnail?.label ||
            null,
        image: normalizeSource(entry.image),
        small_image: normalizeSource(entry.small_image),
        thumbnail: normalizeSource(entry.thumbnail),
        fallback: normalizeSource(entry.fallback)
    }

    if (!normalized.image && !normalized.small_image && !normalized.thumbnail && !normalized.fallback) {
        return null
    }

    return normalized
}

/**
 * Pick the most relevant label for a gallery image based on store information.
 *
 * @param {Object} imageTexts
 * @param {string} identifier
 * @param {Object} item
 * @returns {string|null}
 */
const resolveGalleryLabel = (imageTexts, identifier, item) => {
    if (!imageTexts || typeof imageTexts !== 'object') {
        return null
    }

    const storeIdCandidates = [
        item?.productView?.storeId,
        item?.product?.store_id,
        item?.product?.storeId,
        typeof window !== 'undefined' ? window?.liveSearchConfig?.storeId : null
    ].filter((value) => value !== undefined && value !== null && `${value}` !== '')

    storeIdCandidates.push('0')

    for (const storeId of storeIdCandidates) {
        const label = imageTexts?.[storeId]?.[identifier]
        if (label) {
            return label
        }
    }

    const storeKeys = Object.keys(imageTexts)
    for (const storeId of storeKeys) {
        const fallbackLabel = imageTexts?.[storeId]?.[identifier]
        if (fallbackLabel) {
            return fallbackLabel
        }
    }

    return null
}

/**
 * Normalize gallery node into slide entries keyed by identifier.
 *
 * @param {Object} galleryNode
 * @param {Object} item
 * @returns {Array}
 */
const buildGalleryEntries = (galleryNode, item) => {
    if (!galleryNode || typeof galleryNode !== 'object') {
        return []
    }

    const { imageResolutions, imageTexts } = galleryNode
    if (!imageResolutions || typeof imageResolutions !== 'object') {
        return []
    }

    const variantKeys = ['small_image', 'image', 'thumbnail', 'fallback']
    const entries = new Map()

    Object.values(imageResolutions).forEach((images) => {
        if (!images || typeof images !== 'object') {
            return
        }

        Object.entries(images).forEach(([identifier, data]) => {
            if (!data || typeof data !== 'object' || !data.url || !variantKeys.includes(data.type)) {
                return
            }

            const position = Number.isFinite(data.position) ? Number(data.position) : 0
            const existing = entries.get(identifier) || {
                position,
                label: resolveGalleryLabel(imageTexts, identifier, item),
                image: null,
                small_image: null,
                thumbnail: null,
                fallback: null
            }

            if (!existing.label) {
                existing.label = resolveGalleryLabel(imageTexts, identifier, item)
            }
            existing.position = existing.position || position

            if (!existing[data.type]) {
                existing[data.type] = {
                    url: data.url,
                    width: data.width,
                    height: data.height,
                    format: data.format,
                    position
                }
            }

            entries.set(identifier, existing)
        })
    })

    return Array.from(entries.values()).map((entry, index) => ({
        ...entry,
        position: entry.position || index + 1
    }))
}

/**
 * Select the best matching source for a target size.
 *
 * @param {Object} slide
 * @param {number} targetWidth
 * @param {number} targetHeight
 * @returns {Object|null}
 */
const selectSourceBySize = (slide, targetWidth, targetHeight) => {
    const variants = ['small_image', 'image', 'fallback', 'thumbnail']
    const candidates = variants.map((key) => slide[key]).filter(Boolean)
    const toNumber = (value) => Number.isFinite(Number(value)) ? Number(value) : null

    return candidates.find((src) => toNumber(src.width) === targetWidth &&
        toNumber(src.height) === targetHeight) || null
}

/**
 * Create grouped slide entries from raw attribute data
 *
 * @param {Array} imagesArray
 * @returns {Array}
 */
const buildLiveSearchSlides = (imagesArray) => {
    if (!Array.isArray(imagesArray)) {
        return []
    }

    const slidesMap = new Map()

    const variantKeys = ['small_image', 'image', 'thumbnail', 'fallback']
    const entriesWithUrl = imagesArray.filter((entry) => variantKeys.some((key) => entry?.[key]))
    const variantKeySet = new Set()
    entriesWithUrl.forEach((entry) => {
        variantKeys.forEach((key) => {
            if (entry?.[key]) {
                variantKeySet.add(key)
            }
        })
    })
    const hasExplicitPositions = imagesArray.some(
        (entry) => Number.isFinite(entry?.position) || Number.isFinite(entry?.image?.position)
    )
    const shouldGroupVariants = entriesWithUrl.length > 0 &&
        !hasExplicitPositions &&
        variantKeySet.size === entriesWithUrl.length

    imagesArray.forEach((entry, index) => {
        const normalized = normalizeLiveSearchImageEntry(entry, shouldGroupVariants ? 1 : index + 1)
        if (!normalized) {
            return
        }

        const position = normalized.position || 1
        const slide = slidesMap.get(position) || {
            position,
            label: null,
            image: null,
            small_image: null,
            thumbnail: null,
            fallback: null
        }

        slide.label = slide.label || normalized.label;
        ['image', 'small_image', 'thumbnail', 'fallback'].forEach((key) => {
            if (!slide[key] && normalized[key]) {
                slide[key] = normalized[key]
            }
        })

        slidesMap.set(position, slide)
    })

    return Array.from(slidesMap.values())
        .sort((a, b) => (a.position || 0) - (b.position || 0))
        .map((slide, index) => {
            const smallSource = selectSourceBySize(slide, 360, 540)
            const largeSource = selectSourceBySize(slide, 480, 720)
            const thumbnailSource = slide.thumbnail || null
            const mainImage = largeSource ||
                smallSource ||
                slide.image ||
                slide.small_image ||
                thumbnailSource ||
                slide.fallback
            if (!mainImage?.url) {
                return null
            }

            return {
                position: slide.position || index + 1,
                label: slide.label || null,
                main: mainImage,
                sources: {
                    small: smallSource || slide.small_image || null,
                    large: largeSource || slide.image || null,
                    thumbnail: thumbnailSource || smallSource || largeSource || slide.fallback || null,
                    fallback: slide.fallback || null
                }
            }
        })
        .filter(Boolean)
}

/**
 * Extracts image URLs and slider data from a specific item attribute
 *
 * @param {Object} item
 * @returns {{fallback: (Object|null), large: (Object|null), small: (Object|null), slides: Array, thumbnail: (Object|null)}}
 */
const getImagesFromAttribute = (item) => {
    const rawAttribute = getAttributeValue(item, 'mey_livesearch_images') || null
    let imagesArray = []

    try {
        imagesArray = rawAttribute ? JSON.parse(rawAttribute) : []
    } catch (error) {
        console.error('Unable to parse mey_livesearch_images attribute', error)
    }

    const galleryNode = imagesArray.find((entry) => entry?.gallery)?.gallery || null
    const galleryEntries = buildGalleryEntries(galleryNode, item)
    const legacyEntries = imagesArray.filter((entry) => !entry?.gallery)
    const gallerySlides = buildLiveSearchSlides(galleryEntries)
    const legacySlides = buildLiveSearchSlides(legacyEntries)
    const hasSizedSources = (slidesSet) => slidesSet.some(
        (slide) => slide?.sources?.small?.url || slide?.sources?.large?.url
    )
    let slides = gallerySlides.length ? gallerySlides : legacySlides
    if (legacySlides.length && gallerySlides.length &&
        !hasSizedSources(gallerySlides) &&
        hasSizedSources(legacySlides)) {
        slides = legacySlides
    }
    const primary = slides[0] || { sources: {} }

    return {
        small: primary.sources.small || null,
        large: primary.sources.large || null,
        thumbnail: primary.sources.thumbnail || null,
        fallback: primary.sources.fallback || null,
        slides
    }
}

/**
 * Get image label from productView or use product name as fallback
 * @param item
 * @returns {*}
 */
const getImageLabel = (item) => {
    const first = item?.productView?.images && item.productView.images.length > 0
        ? item.productView.images[0]
        : null
    const label = first && typeof first.label === 'string' && first.label.trim() !== ''
        ? first.label
        : null
    return label ?? item?.product?.name ?? ''
}

/**
 * Check if overlayer attribute is set
 *
 * @param item
 * @param name
 * @returns {*}
 */
const overlayerAttributeIsSet = (item, name) => {
    return item.productView?.attributes?.some(attr => attr.name === name && attr.value === 'yes')
}

/**
 * Get value of an attribute
 *
 * @param item
 * @param attribute
 * @returns {*}
 */
const getAttributeValue = (item, attribute) => {
    return item.productView?.attributes?.find(attr => attr.name === attribute)?.value || null
}

/**
 * Get fake swatches from item attribute
 *
 * @param item
 * @returns {any[]|null}
 */
const getFakeSwatches = (item) => {
    const raw = getAttributeValue(item, 'mey_fake_swatch')
    const parsed = safeJsonParse(raw, null)
    return Array.isArray(parsed) ? parsed : (parsed === null ? null : null)
}

/**
 * Read rating data from the mey_livesearch_rating Catalog Service attribute.
 * Returns null for products without reviews.
 *
 * @param item
 * @returns {{score: number, count: number}|null}
 */
const getRating = (item) => {
    const raw = getAttributeValue(item, 'mey_livesearch_rating')
    if (!raw) {
        return null
    }
    const parsed = safeJsonParse(raw, null)
    if (!parsed || typeof parsed !== 'object') {
        return null
    }
    const score = Number(parsed.score)
    const count = Number(parsed.count)
    return Number.isFinite(score) && Number.isFinite(count) && count > 0
        ? { score, count }
        : null
}

/**
 * Map item to Google Tag Manager data layer format
 *
 * @param item
 * @returns {{is_sale: *, item_color: *, item_design_color_select: *, item_id, item_master_number: *, item_name, item_overlayer_new: *, item_size: string, item_sku: (string|*), item_style: *, magento_id, magento_sku: (string|*), price: (*|number)}}
 */
const mapGtmItem = (item) => {
    return {
        is_sale: overlayerAttributeIsSet(item, 'overlayer_sale'),
        item_color: getAttributeValue(item, 'color'),
        item_design_color_select: getAttributeValue(item, 'design_color_select'),
        item_id: item.product.id,
        item_master_number: getAttributeValue(item, 'master_number'),
        item_name: item.product.name,
        item_overlayer_new: overlayerAttributeIsSet(item, 'overlayer_new'),
        item_size: item.product.size || '',
        item_sku: item.product.sku,
        item_style: getAttributeValue(item, 'style'),
        magento_id: item.product.id,
        magento_sku: item.product.sku,
        price: item?.product?.price_range?.maximum_price?.regular_price?.value ?? 0
    }
}

export {
    getAttributeValue,
    getFakeSwatches,
    getImageLabel,
    getImagesFromAttribute,
    getRating,
    mapGtmItem,
    overlayerAttributeIsSet
}
